import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Bell, Check, Heart, UserPlus, Copy, Star, Trophy, Medal,
  UtensilsCrossed, CalendarClock, Loader2, ArrowLeft, CheckCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";

const NotificationsPage = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch { /* ignore */ }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success("Toutes les notifications marquées comme lues");
    } catch {
      toast.error("Erreur");
    }
  };

  const getIcon = (type) => {
    const map = {
      new_recipe: <UtensilsCrossed className="w-4 h-4 text-primary" />,
      new_follower: <UserPlus className="w-4 h-4 text-secondary" />,
      new_friend: <UserPlus className="w-4 h-4 text-green-500" />,
      friend_request: <UserPlus className="w-4 h-4 text-blue-500" />,
      recipe_liked: <Heart className="w-4 h-4 text-red-500 fill-red-500" />,
      meal_rated: <Star className="w-4 h-4 text-amber-500 fill-amber-500" />,
      meal_invite: <UtensilsCrossed className="w-4 h-4 text-primary" />,
      meal_rsvp: <Check className="w-4 h-4 text-green-500" />,
      meal_reminder: <CalendarClock className="w-4 h-4 text-orange-500" />,
      meal_rate_invite: <Star className="w-4 h-4 text-amber-500" />,
      level_up: <Trophy className="w-4 h-4 text-amber-600" />,
      points_earned: <Medal className="w-4 h-4 text-primary" />,
    };
    return map[type] || <Bell className="w-4 h-4 text-muted-foreground" />;
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return "À l'instant";
    if (mins < 60) return `Il y a ${mins} min`;
    if (hrs < 24) return `Il y a ${hrs}h`;
    if (days < 7) return `Il y a ${days}j`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="notifications-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-muted-foreground hover:text-charcoal transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-heading text-2xl font-bold text-charcoal">Notifications</h1>
              <p className="text-sm text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? "s" : ""}` : "Tout est lu"}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllAsRead} className="gap-1 rounded-full">
              <CheckCheck className="w-4 h-4" />
              Tout lire
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Bell className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">Aucune notification</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {notifications.map((n, i) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
              >
                <Link
                  to={n.link || "#"}
                  onClick={() => { if (!n.read) markAsRead(n.id); }}
                >
                  <Card className={`transition-colors hover:bg-muted/50 ${!n.read ? "border-primary/30 bg-primary/5" : ""}`}>
                    <CardContent className="p-3 flex gap-3 items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        {n.from_user_avatar ? (
                          <ChefAvatar src={n.from_user_avatar} name={n.from_user_pseudo} size="sm" showToque={false} />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            {getIcon(n.type)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.read ? "font-medium" : "text-muted-foreground"}`}>{n.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatTime(n.created_at)}</p>
                      </div>
                      {!n.read && <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />}
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
